import React, { useState } from "react";
import {
  Button,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Text,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  FormLabel,
  Box,
  useDisclosure
} from "@chakra-ui/react";
import { FaMapMarkerAlt, FaFilter, FaSearch } from "react-icons/fa";
import axios from "axios";

const FilterSearch = ({ onFilterApply }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [distance, setDistance] = useState(50); // Default distance
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pinCode, setPincode] = useState('');
  const [search, setSearch] = useState('');
  const [filterDetails, setFilterDetails] = useState({
    search: '',
    latitude: '',
    longitude: '',
    pinCode: '',
    from: '',
    to: '',
    distance: ''
  });
  const [lastInput, setLastInput] = useState(''); // New state to track the last input

  const locationButton = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      setFilterDetails((prevDetails) => ({
        ...prevDetails,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        pinCode: '' // Clear pinCode when geolocation is used
      }));
      setLastInput('geolocation'); // Update last input
      console.log('Geolocation set:', filterDetails);
    });
  };

  const handlePincodeChange = (e) => {
    setPincode(e.target.value);
    setFilterDetails((prevDetails) => ({
      ...prevDetails,
      pinCode: e.target.value,
      latitude: '', 
      longitude: ''
    }));
    setLastInput('pincode');
    
    // Validate PIN code format
    const isUKPostcode = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i.test(e.target.value);
    const isIndianPincode = /^[1-9][0-9]{5}$/.test(e.target.value);
    
    if (!isUKPostcode && !isIndianPincode && e.target.value.trim() !== '') {
      // Show validation error
      console.log("Invalid postal/PIN code format");
    }
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    if (new Date(e.target.value) > new Date(toDate)) {
      setToDate(e.target.value);
    }
  };

  const handleToDateChange = (e) => {
    if (new Date(e.target.value) >= new Date(fromDate)) {
      setToDate(e.target.value);
    }
  };

  const filterApply = async (event) => {
    event.preventDefault();
    const updatedFilterDetails = {
      ...filterDetails,
      from: fromDate,
      to: toDate,
      distance: distance,
      search: search,
      pinCode: lastInput === 'pincode' ? pinCode : '',
      latitude: lastInput === 'geolocation' ? filterDetails.latitude : '',
      longitude: lastInput === 'geolocation' ? filterDetails.longitude : ''
    };

    console.log("Applying filters with details:", updatedFilterDetails);
    const ticketToken = sessionStorage.getItem('ticketToken');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/applicant/get-filtered-jobs`,
        updatedFilterDetails,
        {
          headers: {
            Authorization: `Bearer ${ticketToken}`
          }
        }
      );
      console.log('Response data:', response.data); // Log the response data
      if (response.data.data) {
        onFilterApply(response.data.data); // Pass filtered jobs to JobList
      } else {
        onFilterApply([]); // Pass empty array if no jobs found
      }
      onClose(); // Close the popover after applying filters
    } catch (error) {
      console.error('Error submitting filter data:', error);
      onFilterApply([]); // Pass empty array on error
      onClose(); // Close the popover on error
    }
  };

  return (
    <Flex justifyContent="flex-end" alignItems="center" p={4}>
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom-end"
        closeOnBlur={true} // Close popover when clicking outside
      >
        <PopoverTrigger>
          <Button
            colorScheme="blue"
            size="md"
            boxShadow="md"
            bg="blue.500"
            _hover={{ bg: "blue.600" }}
            _active={{ bg: "blue.700" }}
            color="white"
            leftIcon={<Icon as={FaFilter} />}
            onClick={onOpen}
            mt={-4} // Adjust this value to move the button upward
          >
            Filter
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader>Filter Options</PopoverHeader>
          <PopoverBody>
            <Flex direction="column" gap={2}>
              <Box>
                <FormLabel mb={0}>Search</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.300" />
                  </InputLeftElement>
                  <Input
                    type="text"
                    placeholder="Search jobs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    focusBorderColor="transparent"
                    variant="filled"
                    _focus={{ boxShadow: "none", outline: "none" }}
                    size="md"
                  />
                </InputGroup>
              </Box>
              <Box>
                <FormLabel mb={0}>Find by: Pincode</FormLabel>
                <InputGroup>
                  <Input
                    type="text"
                    placeholder="Pincode"
                    value={pinCode}
                    onChange={handlePincodeChange} // Use the new handler
                    focusBorderColor="transparent"
                    variant="filled"
                    _focus={{ boxShadow: "none", outline: "none" }}
                    size="md"
                  />
                  <Button
                    colorScheme="blue"
                    size="md"
                    boxShadow="md"
                    bg="blue.500"
                    _hover={{ bg: "blue.600" }}
                    _active={{ bg: "blue.700" }}
                    color="white"
                    leftIcon={<Icon position="center" as={FaMapMarkerAlt} />}
                    onClick={locationButton}
                  />
                </InputGroup>
              </Box>
              <Box>
                <FormLabel mb={0}>From</FormLabel>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  focusBorderColor="transparent"
                  variant="filled"
                  _focus={{ boxShadow: "none", outline: "none" }}
                  size="md"
                />
              </Box>
              <Box>
                <FormLabel mb={0}>To</FormLabel>
                <Input
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  min={fromDate} // Ensure "To" date is not before "From" date
                  focusBorderColor="transparent"
                  variant="filled"
                  _focus={{ boxShadow: "none", outline: "none" }}
                  size="md"
                />
              </Box>
              <Box>
                <FormLabel mb={0}>Distance</FormLabel>
                <Slider
                  defaultValue={50}
                  min={20}
                  max={300}
                  step={10}
                  value={distance}
                  onChange={(val) => setDistance(val)}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={5}>
                    <Box color="blue.500" as={FaMapMarkerAlt} />
                  </SliderThumb>
                </Slider>
                <Text mt={2} fontSize="sm" textAlign="center">
                  {distance} km
                </Text>
              </Box>
              <Button
                colorScheme="blue"
                size="md"
                boxShadow="md"
                bg="blue.500"
                _hover={{ bg: "blue.600" }}
                _active={{ bg: "blue.700" }}
                color="white"
                width="100%"
                onClick={filterApply} // Call filterApply on click
              >
                Apply
              </Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Flex>
  );
};

export default FilterSearch;